import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { usePublicAccountSetupDetails } from 'domains/public/components/complete-account-setup/utils/usePublicAccountSetupDetails.hooks'
import { LinkButton } from 'gin-and-tonic/routing/link-button/LinkButton'
import { PublicLayout } from 'domains/public/components/layout/PublicLayout'
import { Input } from 'gin-and-tonic/common/input/Input'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import { Button } from 'gin-and-tonic/common/button/Button'
import { type DashRouteComponentProps } from 'legacy/dash-components/dash-route/utils/dashRoute.types'

interface Props extends DashRouteComponentProps<{ token: string }> {}

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  width: 240,
  justifyItems: 'flex-start',
})

export const PublicCompleteAccountSetup: FunctionComponent<Props> = ({
  match: {
    params: { token },
  },
}) => {
  const {
    onSubmit,
    passwordFieldRef,
    confirmPasswordFieldRef,
    givenNameFieldRef,
    surnameFieldRef,
    passwordErrorMessageId,
    confirmPasswordErrorMessageId,
    givenNameErrorMessageId,
    surnameErrorMessageId,
    disableFormFields,
    accountSetupSuccess,
    tokenErrorMessageId,
    formErrorMessageId,
    reCaptchaElement,
  } = usePublicAccountSetupDetails({
    token,
  })

  const testId = 'public-account-setup-layout'

  if (accountSetupSuccess) {
    return (
      <PublicLayout
        headerMessageId="public.accountSetup.header"
        contentMessageId="public.accountSetup.content.success"
        data-testid={testId}
      >
        <LinkButton
          messageId="public.accountSetup.successAction"
          toRoute="app-halt-redirect"
          data-testid="public-account-setup-success-action-button"
        />
      </PublicLayout>
    )
  }

  if (tokenErrorMessageId) {
    return (
      <PublicLayout
        headerMessageId="public.accountSetup.header"
        contentMessageId={tokenErrorMessageId}
        data-testid={testId}
      >
        <LinkButton
          messageId="public.accountSetup.errorAction"
          toRoute="public-request-account-setup"
          data-testid="public-account-setup-error-action-button"
        />
      </PublicLayout>
    )
  }

  return (
    <PublicLayout headerMessageId="public.accountSetup.header">
      <form css={formStyles} onSubmit={onSubmit}>
        {/* Auth Service stores names as one fullname field. Piggy back on given name for now */}
        <Input
          name="givenName"
          disabled={disableFormFields}
          errorMessageId={givenNameErrorMessageId}
          placeholderMessageId="public.createAccount.form.fields.givenName.placeholder"
          ref={givenNameFieldRef}
          data-testid="public-create-account-form-field-given-name"
        />
        <Input
          name="surname"
          value=""
          hidden
          disabled={disableFormFields}
          errorMessageId={surnameErrorMessageId}
          placeholderMessageId="public.createAccount.form.fields.surname.placeholder"
          ref={surnameFieldRef}
          data-testid="public-create-account-form-field-surname"
        />
        <Input
          name="password"
          type="password"
          disabled={disableFormFields}
          errorMessageId={passwordErrorMessageId}
          placeholderMessageId="public.createAccount.form.fields.password.placeholder"
          ref={passwordFieldRef}
          data-testid="public-create-account-form-field-password"
        />
        <Input
          name="confirmPassword"
          type="password"
          disabled={disableFormFields}
          errorMessageId={confirmPasswordErrorMessageId}
          placeholderMessageId="public.createAccount.form.fields.confirmPassword.placeholder"
          ref={confirmPasswordFieldRef}
          data-testid="public-create-account-form-field-confirm-password"
        />

        {reCaptchaElement}

        <ErrorMessage
          messageId={formErrorMessageId}
          data-testid="public-create-account-form-error-message"
        />
        <Button
          disabled={disableFormFields}
          type="submit"
          messageId="public.createAccount.form.submit"
          data-testid="public-create-account-form-submit"
        />
      </form>
    </PublicLayout>
  )
}
